import { useSignal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { Dialog, ToastRegion } from '@amumin/ui';
import { SpaceSwitcher } from './SpaceSwitcher.js';
import { cx, Icon } from '@amumin/ui-core';

import { api } from '../api.js';
import { HOUSEHOLD_TZ, zoneDiffers } from '../lib/dates.js';
import {
  ADMIN_NAV,
  activePersonal,
  isActive,
  isRelative,
  itemHref,
  moreGroups,
  navLabel,
  PERSONAL_GROUP,
  relativeItems,
  sidebarItems,
  tabbarItems,
  type NavIcon,
  type NavItem,
} from '../lib/navigation.js';
import { topBack } from '../lib/pageContext.js';
import {
  appText,
  approvalsCount,
  bumpData,
  context,
  household,
  loadHousehold,
  logout,
  messages,
  refreshApprovals,
  setNavigate,
  sharedComputer,
} from '../state/session.js';
import { loadModules } from '../state/modules.js';
import { loadRelativeRights, relativeSeesAgenda } from '../state/care.js';
import { DeletionBanner } from '../privacy/common.js';
import { refreshDeletion } from '../privacy/deletionState.js';
import { BrandLogo } from './BrandLogo.js';
import { PausedBanner } from './PausedBanner.js';
import { BillingBanner } from '../billing/BillingBanner.js';
import { PushResumeBanner } from './PushResumeBanner.js';
import { StepUpDialog } from './StepUpDialog.js';
import { MoneyGlyph } from '../money/glyph.js';
import { FrameGlyph } from '../photoFrame/FrameGlyph.js';
import { KompasGlyph } from '../kompas/KompasGlyph.js';

// Schil van de ingelogde app (WP2, docs/ux/compact-plan.md hoofdstuk 3; besluit oprichter 5 okt):
// vaste plekken plus Meer, op de telefoon en de computer in dezelfde volgorde. Op de telefoon een
// onderbalk (Vandaag, Agenda, Taken, Eten en Meer) met Meer als blad. Restpunten (docs/ux/restpunten,
// onderzoek professioneel-bord: "de zijbalk naar 6 hoofdpunten plus Meer"): op de computer de
// zijbalk met dezelfde plekken plus Prikbord en een knop Meer die hetzelfde blad opent (zes punten);
// staat je pagina onder Meer, dan staat die eronder ingesprongen. Beheer (platformbeheerder) blijft
// onderaan. Het model (welke plekken, welke tabbladen, oude adressen) staat in lib/navigation.ts.

/**
 * Pictogram van Board: een bord aan de muur (liggende tablet met een rustige indeling: een kolom
 * agenda en een blok ernaast) aan een ophangpunt. Zelfde lijnstijl als de sprite (24 × 24, lijn 2,
 * ronde uiteinden); eigen tekening, geen standaard-icoonset.
 */
function BoardGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M12 2.5v2" />
      <rect x="2.5" y="4.5" width="19" height="14" rx="2.5" />
      <path d="M6.5 9h5" />
      <path d="M6.5 12.5h3.5" />
      <rect x="14" y="8.5" width="4" height="6" rx="1" />
      <path d="M8 21.5h8" />
    </svg>
  );
}

/**
 * Pictogram van Notities (0060) en Prikbord: een briefje met een omgevouwen hoek en twee regels.
 * Zelfde lijnstijl als BoardGlyph; eigen tekening, geen standaard-icoonset.
 */
function NoteGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M4.5 3.5h15v11l-5 5h-10z" />
      <path d="M19.5 14.5h-5v5" />
      <path d="M8 8.5h8" />
      <path d="M8 12h5" />
    </svg>
  );
}

function NavGlyph({ icon, size }: { icon: NavIcon; size: number }) {
  if (icon === 'board') return <BoardGlyph size={size} />;
  if (icon === 'note') return <NoteGlyph size={size} />;
  if (icon === 'frame') return <FrameGlyph size={size} />;
  if (icon === 'kompas') return <KompasGlyph size={size} />;
  if (icon === 'money') return <MoneyGlyph size={size} />;
  return <Icon id={icon} size={size} />;
}

function NavLink({
  item,
  path,
  compact = false,
  sub = false,
}: {
  item: NavItem;
  path: string;
  compact?: boolean;
  /** Restpunten: de pagina onder Meer waar je nu bent, ingesprongen onder de knop Meer. */
  sub?: boolean;
}) {
  // Voor jou (WP2): wat op goedkeuring wacht, telt bij Taken (daar staat Sterren); geen losse
  // inbox meer bovenin en geen stip bij Meer.
  const count = item.key === 'tasks' ? approvalsCount.value : 0;
  return (
    <a
      href={itemHref(item)}
      class={cx('nav-link', compact && 'nav-link-compact', sub && 'nav-link-sub')}
      aria-current={isActive(path, item) ? 'page' : undefined}
      data-nav={item.key}
    >
      <span class="nav-icon">
        <NavGlyph icon={item.icon} size={compact ? 24 : 20} />
        {count > 0 && compact ? <span class="nav-dot" aria-hidden="true" /> : null}
      </span>
      <span class="nav-label">{navLabel(item.key)}</span>
      {count > 0 && !compact ? (
        <span
          class="nav-count"
          aria-label={appText.value.nav.forYouWaiting.replace('{n}', String(count))}
        >
          {count}
        </span>
      ) : null}
    </a>
  );
}

/** Terug op de telefoon: de pijl en de naam van de ouderpagina in de bovenbalk. */
function TopBackLink() {
  const back = topBack.value;
  if (!back) return null;
  const inner = (
    <>
      <Icon id="arrow-left" size={22} />
      <span class="topbar-back-label">{back.label}</span>
    </>
  );
  return back.href ? (
    <a class="topbar-back" href={back.href} data-name="topbar-back">
      {inner}
    </a>
  ) : (
    <button
      type="button"
      class="topbar-back"
      data-name="topbar-back"
      onClick={() => back.onPress?.()}
    >
      {inner}
    </button>
  );
}

/**
 * Pagina's die op een groot scherm de hele breedte van het kader gebruiken (feat/vandaag-raster):
 * meer kolommen en kaarten. De rest (formulieren, instellingen, lange tekst) houdt zijn inhoud op
 * 1120 px, links uitgelijnd in hetzelfde kader (styles/shell.css), dus de titel staat overal gelijk.
 */
const WIDE_ROUTES = ['/vandaag', '/agenda', '/taken', '/eten', '/geldzaken', '/kompas'];

export function isWideRoute(path: string): boolean {
  return (
    path === '/instellingen' || WIDE_ROUTES.some((p) => path === p || path.startsWith(`${p}/`))
  );
}

export function Shell({ children }: { children: ComponentChildren }) {
  const { path, route } = useLocation();
  const m = messages.value;
  const a = appText.value;
  const ctx = context.value;
  const hh = household.value;
  const tz = hh?.timezone ?? HOUSEHOLD_TZ;
  const showTz = zoneDiffers(tz);
  const moreOpen = useSignal(false);
  // Welke knop Meer opende het blad: de onderbalk (telefoon) of de zijbalk (computer).
  const moreFrom = useSignal<'tabbar' | 'sidebar'>('tabbar');
  const online = useSignal(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    setNavigate(route);
  }, [route]);

  useEffect(() => {
    if (!household.value) loadHousehold().catch(() => undefined);
    // 0029: welke modules aan staan (navigatie, schermen); bij een wissel opnieuw.
    void loadModules();
    void refreshApprovals();
    const timer = setInterval(() => void refreshApprovals(), 60_000);
    // Geen verbinding (DESIGN.md §8.X): rustige melding bovenaan; weer online → alles verversen.
    const goOffline = () => (online.value = false);
    const goOnline = () => {
      online.value = true;
      bumpData();
      void refreshApprovals();
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      clearInterval(timer);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  // 0036: geplande verwijdering van het gezinsbord (banner); opnieuw bij een ander gezin.
  useEffect(() => {
    void refreshDeletion();
  }, [ctx?.active_household_id]);

  // Golf 8: een meekijker — welke rechten heeft hij hier (Agenda in de navigatie)?
  const relative = isRelative();
  useEffect(() => {
    if (relative) void loadRelativeRights();
    else relativeSeesAgenda.value = false;
  }, [ctx?.active_household_id, relative]);

  useEffect(() => {
    moreOpen.value = false;
    // Nieuwe pagina: focus naar de inhoud (schermlezers) zonder te scrollen.
    document.getElementById('main')?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [path]);

  const switchHousehold = async (householdId: string) => {
    if (householdId === 'personal') await api.app.auth.personalSpace();
    else await api.app.household.switch(householdId);
    // Clear all module caches and open editors together when changing privacy scope.
    window.location.assign('/vandaag');
  };

  const householdLabel = (cls: string) =>
    ctx &&
    (ctx.households.length > 1 ||
      ctx.households.some((h) => h.role === 'owner' || h.role === 'parent')) ? (
      <SpaceSwitcher
        spaces={ctx.households}
        activeId={ctx.active_household_id}
        onSwitch={switchHousehold}
      />
    ) : (
      <span class={cls}>{hh?.name ?? ''}</span>
    );

  const admin = ctx?.platform_admin === true;
  const bar = tabbarItems();
  const side = sidebarItems();
  const inBar = new Set(bar.map((i) => i.href));
  // Meer: wat niet vast in de onderbalk of de zijbalk staat; een meekijker alleen wat niet in de
  // onderbalk staat (in de zijbalk staat zijn hele lijst, zonder Meer).
  const barGroups: NavItem[][] = relative
    ? [relativeItems().filter((i) => !inBar.has(i.href))]
    : moreGroups(bar);
  const sideGroups: NavItem[][] = relative ? [] : moreGroups(side);
  const groups = moreFrom.value === 'sidebar' ? sideGroups : barGroups;
  const moreActive = [...barGroups.flat(), ...(admin ? ADMIN_NAV : [])].some((i) =>
    isActive(path, i),
  );
  // De pagina onder Meer waar je nu bent (zijbalk): ingesprongen onder Meer, zodat je ziet waar je
  // bent. Beheer staat onderaan en telt hier niet.
  const sideActive = sideGroups.flat().find((i) => isActive(path, i));
  const openMore = (from: 'tabbar' | 'sidebar') => {
    moreFrom.value = from;
    moreOpen.value = true;
  };
  const personal = activePersonal();

  /** De groepen van Meer: in de zijbalk onder de vaste plekken, op de telefoon in het blad. */
  const groupNav = (where: 'sidebar' | 'more') =>
    groups.map((group, index) => {
      if (group.length === 0) return null;
      const labelled = !relative && index === PERSONAL_GROUP;
      const labelId = `nav-personal-${where}`;
      return (
        <div
          class={cx('nav-group', where === 'more' && 'more-group')}
          key={`${where}-${index}`}
          role="group"
          aria-labelledby={labelled ? labelId : undefined}
          data-name={`nav-group-${index}`}
        >
          {labelled ? (
            <p class="nav-group-label" id={labelId}>
              {a.omgeving.spaces.mine}
            </p>
          ) : null}
          {group.map((item) => (
            <NavLink key={item.href} item={item} path={path} />
          ))}
        </div>
      );
    });

  /**
   * Blokje "Beheer" (alleen voor de platformbeheerder): in de zijbalk een eigen navigatie, in
   * "Meer" een groep binnen de lijst (geen navigatie in een navigatie).
   */
  const adminNav = (where: 'sidebar' | 'more') => {
    if (!admin) return null;
    const labelId = `nav-admin-label-${where}`;
    const items = ADMIN_NAV.map((item) => <NavLink key={item.href} item={item} path={path} />);
    return where === 'sidebar' ? (
      <nav class="nav-admin" aria-labelledby={labelId} data-name="nav-admin">
        <p class="nav-admin-label" id={labelId}>
          {a.nav.admin}
        </p>
        {items}
      </nav>
    ) : (
      <div class="nav-admin" role="group" aria-labelledby={labelId} data-name="nav-admin-more">
        <p class="nav-admin-label" id={labelId}>
          {a.nav.admin}
        </p>
        {items}
      </div>
    );
  };

  return (
    <div class="shell" data-space={personal ? 'personal' : undefined}>
      <a class="skip-link" href="#main">
        {a.nav.skip}
      </a>
      <aside class={cx('sidebar', admin && 'sidebar-admin')} aria-label={a.nav.main}>
        <div class="brand">
          <a class="brand-logo-link" href="/vandaag" aria-label={m.web.appName}>
            <BrandLogo />
          </a>
          <span class="brand-text">{householdLabel('shell-household')}</span>
        </div>
        <nav class="nav-list" aria-label={a.nav.main} data-role={relative ? 'relative' : undefined}>
          {relative ? (
            relativeItems().map((item) => <NavLink key={item.href} item={item} path={path} />)
          ) : (
            <>
              <div class="nav-group" data-name="nav-primary">
                {side.map((item) => (
                  <NavLink key={item.href} item={item} path={path} />
                ))}
                <button
                  type="button"
                  class="nav-link nav-button"
                  aria-expanded={moreOpen.value && moreFrom.value === 'sidebar' ? 'true' : 'false'}
                  aria-current={sideActive ? 'page' : undefined}
                  onClick={() => openMore('sidebar')}
                  data-name="sidebar-more"
                >
                  <span class="nav-icon">
                    <Icon id="menu" size={20} />
                  </span>
                  <span class="nav-label">{a.nav.more}</span>
                </button>
                {sideActive ? <NavLink item={sideActive} path={path} sub /> : null}
              </div>
            </>
          )}
        </nav>
        <div class="sidebar-foot">
          {showTz ? (
            <span class="tz-badge" title={m.web.browserZoneDiffers} data-name="tz-badge">
              {m.web.timeInAmsterdam}
            </span>
          ) : null}
          {adminNav('sidebar')}
          <button
            type="button"
            class="nav-link nav-button"
            data-name="logout"
            onClick={() => void logout(false)}
          >
            <span class="nav-icon">
              <Icon id="log-out" size={20} />
            </span>
            <span class="nav-label">{a.nav.logout}</span>
          </button>
        </div>
      </aside>

      <header class={cx('topbar', topBack.value && 'topbar-with-back')}>
        {topBack.value ? (
          <TopBackLink />
        ) : (
          <div class="brand brand-compact">
            <a class="brand-logo-link" href="/vandaag" aria-label={m.web.appName}>
              <BrandLogo />
            </a>
            {householdLabel('topbar-household')}
          </div>
        )}
        <span class="shell-spacer" />
        {showTz ? (
          <span class="tz-badge" title={m.web.browserZoneDiffers} data-name="tz-badge-mobile">
            {m.web.timeInAmsterdam}
          </span>
        ) : null}
      </header>

      <div class="shell-body">
        {sharedComputer.value ? <div class="banner">{m.web.forgetToLogout}</div> : null}
        {/* 0035: gepauzeerd door AMUMIN Beheer (alleen kijken). */}
        <PausedBanner />
        {/* 0065: proefmaand voorbij → alleen kijken, met één knop naar betalen. */}
        <BillingBanner path={path} />
        <DeletionBanner />
        <PushResumeBanner />
        {!online.value ? (
          <div class="offline-banner" role="status" data-name="offline">
            <Icon id="cloud-off" size={18} />
            {a.offline}
          </div>
        ) : null}
        {/* Eén paginakader (WP1/WP2): de titel staat op elke pagina op dezelfde plek. Mijn omgeving
            is een label onder de titel (lib/ui.tsx PageHeader), geen banner erboven. */}
        <main
          class="shell-main"
          id="main"
          tabIndex={-1}
          data-width={isWideRoute(path) ? 'wide' : undefined}
        >
          {children}
        </main>
      </div>

      <nav class="tabbar" aria-label={a.nav.main}>
        {bar.map((item) => (
          <NavLink key={item.href} item={item} path={path} compact />
        ))}
        <button
          type="button"
          class="nav-link nav-link-compact nav-button"
          aria-expanded={moreOpen.value ? 'true' : 'false'}
          aria-current={moreActive ? 'page' : undefined}
          onClick={() => openMore('tabbar')}
          data-nav="more"
        >
          <span class="nav-icon">
            <Icon id="menu" size={24} />
          </span>
          <span class="nav-label">{a.nav.more}</span>
        </button>
      </nav>

      <Dialog
        open={moreOpen.value}
        onClose={() => (moreOpen.value = false)}
        variant="sheet"
        title={a.nav.more}
        closeLabel={m.design.dialog.close}
        name="more-menu"
        // Een kort menu: volledig scherm heeft hier geen zin.
        expandable={false}
      >
        <nav class="more-list" aria-label={a.nav.more}>
          {groupNav('more')}
          {adminNav('more')}
          <button
            type="button"
            class="nav-link nav-button more-logout"
            onClick={() => void logout(false)}
          >
            <span class="nav-icon">
              <Icon id="log-out" size={20} />
            </span>
            <span class="nav-label">{a.nav.logout}</span>
          </button>
        </nav>
      </Dialog>

      <ToastRegion dismissLabel={m.design.toast.dismiss} />
      <StepUpDialog />
    </div>
  );
}
